/**
 * DebitOrderSetup — NuPay-manual DebiCheck setup
 *
 * Student submits bank details → GenLend stores pending mandate →
 * admin registers on NuPay → student approves in banking app →
 * admin marks authorised in GenLend → student notified.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { debicheckAPI, loanAPI, DebiCheckMandate } from '../../services/api';
import './DebitOrderSetup.css';

type Step = 'FORM' | 'SUBMITTED' | 'AUTHENTICATED' | 'ERROR';

interface FormState {
  accountHolder: string;
  bankName: string;
  accountNumber: string;
  branchCode: string;
  accountType: string;
  debitDay: string;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
}

function formatCollectionDate(d?: string | null): string {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const DebitOrderSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState<Step>('FORM');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loadingLoan, setLoadingLoan] = useState(true);
  const [loanId, setLoanId] = useState<string | null>(null);
  const [loanNumber, setLoanNumber] = useState('');
  const [loanTotal, setLoanTotal] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [mandate, setMandate] = useState<DebiCheckMandate | null>(null);
  const [mandateAccepted, setMandateAccepted] = useState(false);

  const [formData, setFormData] = useState<FormState>({
    accountHolder: '',
    bankName: '',
    accountNumber: '',
    branchCode: '',
    accountType: 'savings',
    debitDay: '25',
  });

  useEffect(() => {
    if (!user) return;
    setFormData((prev) => ({
      ...prev,
      accountHolder:
        prev.accountHolder ||
        `${user.first_name || ''} ${user.last_name || ''}`.trim(),
      bankName: prev.bankName || (user as any).bank_name || '',
      accountNumber: prev.accountNumber || (user as any).bank_account_number || '',
      branchCode: prev.branchCode || (user as any).bank_branch_code || '',
    }));
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoadingLoan(true);
        const paymentsRes = await loanAPI.getStudentPayments();
        if (cancelled) return;

        if (!paymentsRes.success || !paymentsRes.data) {
          setError('We could not load your loan details. Please try again.');
          return;
        }

        const eligibleLoan = (paymentsRes.data.loans || []).find(
          (l: any) =>
            ['approved', 'disbursed', 'overdue'].includes(l.status) &&
            (l.outstandingBalance ?? l.totalAmount ?? 0) > 0
        );

        if (!eligibleLoan) {
          setError(
            "We couldn't find an approved or active loan for DebiCheck setup."
          );
          return;
        }

        setLoanId(eligibleLoan.id);
        setLoanNumber(eligibleLoan.loanNumber || '');
        setLoanTotal(Number(eligibleLoan.totalAmount ?? 0));

        try {
          const existingRes = await debicheckAPI.getMandateForLoan(eligibleLoan.id);
          const existing = existingRes.data;
          if (existing?.mandateId) {
            setMandate(existing);
            if (
              existing.status === 'authenticated' ||
              existing.status === 'rms_registered'
            ) {
              setStep('AUTHENTICATED');
            } else if (existing.status === 'pending') {
              setStep('SUBMITTED');
            }
          }
        } catch {
          // no existing mandate
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message || 'Could not load your loan details.'
          );
        }
      } finally {
        if (!cancelled) setLoadingLoan(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const perInstalment = useMemo(() => {
    if (!loanTotal) return null;
    return Math.round((loanTotal / 3) * 100) / 100;
  }, [loanTotal]);

  const validateField = (name: string, value: string): string | null => {
    switch (name) {
      case 'accountHolder':
        return value.trim().length < 2 ? 'Enter the account holder name' : null;
      case 'bankName':
        return !value ? 'Select a bank' : null;
      case 'accountNumber':
        return !/^\d{8,12}$/.test(value) ? 'Account number must be 8–12 digits' : null;
      case 'branchCode':
        return !/^\d{6}$/.test(value) ? 'Branch code must be 6 digits' : null;
      default:
        return null;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const fieldError = validateField(name, value);
    if (fieldError) {
      setFieldErrors((prev) => ({ ...prev, [name]: fieldError }));
    } else {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!loanId) {
      setError('No eligible loan found. Please refresh and try again.');
      return;
    }
    if (!mandateAccepted) {
      setError('You must accept the mandate authorisation to proceed.');
      return;
    }

    const validationErrors: Record<string, string> = {};
    (['accountHolder', 'bankName', 'accountNumber', 'branchCode'] as const).forEach(
      (f) => {
        const err = validateField(f, formData[f]);
        if (err) validationErrors[f] = err;
      }
    );
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    try {
      setSubmitting(true);
      const response = await debicheckAPI.initiateMandate({
        loanId,
        accountHolder: formData.accountHolder,
        bankName: formData.bankName,
        accountNumber: formData.accountNumber,
        branchCode: formData.branchCode,
        accountType: formData.accountType,
        debitDay: formData.debitDay,
      });

      if (!response.success) {
        setError('Failed to submit. Please try again.');
        return;
      }

      setMandate({
        mandateId: response.data.mandateId,
        status: response.data.status,
        collectionAmount: response.data.collectionAmount,
        firstCollectionDate: response.data.firstCollectionDate,
        collectionDayCode: response.data.collectionDayCode,
        accountReference: response.data.accountReference,
        mode: response.data.mode as any,
      });
      setStep(
        response.data.status === 'authenticated' ? 'AUTHENTICATED' : 'SUBMITTED'
      );
    } catch (err: any) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      const existing = body?.data;

      if (status === 409 && existing?.mandateId) {
        setMandate({
          mandateId: existing.mandateId,
          status: existing.status,
          collectionAmount: existing.collectionAmount ?? 0,
          firstCollectionDate: existing.firstCollectionDate,
          collectionDayCode: existing.collectionDayCode,
          accountReference: existing.accountReference,
        });
        setStep(
          existing.status === 'authenticated' || existing.status === 'rms_registered'
            ? 'AUTHENTICATED'
            : 'SUBMITTED'
        );
        return;
      }

      if (status === 202 && existing?.mandateId) {
        setMandate({
          mandateId: existing.mandateId,
          status: existing.status || 'pending',
          collectionAmount: existing.collectionAmount ?? 0,
          firstCollectionDate: existing.firstCollectionDate,
          collectionDayCode: existing.collectionDayCode,
          accountReference: existing.accountReference,
        });
        setStep('SUBMITTED');
        return;
      }

      setError(
        body?.message || body?.error || err?.message || 'Something went wrong.'
      );
      setStep('ERROR');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setStep('FORM');
    setError(null);
    setMandate(null);
    setMandateAccepted(false);
  };

  if (loadingLoan) {
    return (
      <div className="debit-order-setup">
        <div className="setup-header">
          <button type="button" onClick={() => navigate(-1)} className="back-btn">
            ← Back
          </button>
          <h1>Set up debit order</h1>
        </div>
        <div className="setup-container">
          <p className="loading-text">Loading your loan details…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="debit-order-setup">
      <div className="setup-header">
        <button type="button" onClick={() => navigate(-1)} className="back-btn">
          ← Back
        </button>
        <h1>Set up debit order (DebiCheck)</h1>
        <p>Share your bank details so GenLend can register automatic repayments</p>
      </div>

      <div className="setup-container">
        {step === 'FORM' && (
          <form onSubmit={handleSubmit} className="setup-form">
            {error && <div className="error-banner">{error}</div>}

            <div className="debit-order-mock-notice" role="status">
              <strong>How this works:</strong> After you submit, GenLend registers
              your details on NuPay. Your bank will then ask you to approve the
              debit order in your banking app. We will SMS/email you once it is
              authorised. You can still pay installments with PayFast anytime.
            </div>

            {loanNumber && (
              <div className="loan-summary-strip">
                <div>
                  <span className="loan-summary-label">Loan</span>
                  <span className="loan-summary-value">{loanNumber}</span>
                </div>
                <div>
                  <span className="loan-summary-label">Total to repay</span>
                  <span className="loan-summary-value">{formatCurrency(loanTotal)}</span>
                </div>
                {perInstalment && (
                  <div>
                    <span className="loan-summary-label">Per instalment</span>
                    <span className="loan-summary-value">
                      {formatCurrency(perInstalment)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="form-section">
              <h3>Banking Details</h3>
              <p className="field-hint" style={{ marginTop: 0 }}>
                Repayments are collected from the same account the loan is paid into. These details come from your application.
              </p>

              <div className="form-group">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  name="accountHolder"
                  value={formData.accountHolder}
                  onChange={handleChange}
                  required
                  readOnly
                  placeholder="As it appears on the account"
                />
                {fieldErrors.accountHolder && (
                  <span className="field-error">{fieldErrors.accountHolder}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name</label>
                  <select
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    required
                    disabled
                  >
                    <option value="">Select Bank</option>
                    <option value="Capitec">Capitec</option>
                    <option value="FNB">FNB</option>
                    <option value="Standard Bank">Standard Bank</option>
                    <option value="Absa">Absa</option>
                    <option value="Nedbank">Nedbank</option>
                    <option value="TymeBank">TymeBank</option>
                    <option value="Investec">Investec</option>
                  </select>
                  {fieldErrors.bankName && (
                    <span className="field-error">{fieldErrors.bankName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Account Type</label>
                  <select
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleChange}
                    required
                    disabled
                  >
                    <option value="savings">Savings</option>
                    <option value="cheque">Cheque/Current</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    readOnly
                    required
                    pattern="[0-9]{8,12}"
                    placeholder="e.g. 1234567890"
                    maxLength={12}
                    inputMode="numeric"
                  />
                  {fieldErrors.accountNumber && (
                    <span className="field-error">{fieldErrors.accountNumber}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Branch Code</label>
                  <input
                    type="text"
                    name="branchCode"
                    value={formData.branchCode}
                    onChange={handleChange}
                    readOnly
                    required
                    pattern="[0-9]{6}"
                    placeholder="e.g. 250655"
                    maxLength={6}
                    inputMode="numeric"
                  />
                  {fieldErrors.branchCode && (
                    <span className="field-error">{fieldErrors.branchCode}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Preferred Debit Day</label>
                <select
                  name="debitDay"
                  value={formData.debitDay}
                  onChange={handleChange}
                  required
                >
                  <option value="1">1st of the month</option>
                  <option value="15">15th of the month</option>
                  <option value="25">25th of the month</option>
                  <option value="last">Last day of the month</option>
                </select>
                <span className="help-text">
                  Your instalments will be collected on this day each month.
                </span>
              </div>
            </div>

            <div className="mandate-section">
              <h3>Mandate Authorisation (DebiCheck)</h3>
              <div className="mandate-box">
                <p>
                  <strong>Authority and Mandate for payment instructions</strong>
                </p>
                <p>
                  I authorise <strong>GenLend</strong> (via NuPay DebiCheck) to
                  issue payment instructions to my bank for collection against my
                  account on my chosen debit day, provided such instructions do
                  not exceed my repayment obligation for this loan.
                </p>
                <p>
                  After GenLend registers this mandate, my bank will ask me to
                  approve it in my banking app. Collections only begin once I
                  have approved it there and GenLend has confirmed authorisation.
                </p>
              </div>

              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={mandateAccepted}
                  onChange={(e) => setMandateAccepted(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span className="label-text">
                  I have read and accept the DebiCheck mandate authorisation.
                </span>
              </label>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="submit-btn"
                disabled={submitting || !mandateAccepted || !loanId}
              >
                {submitting ? 'Submitting…' : 'Submit debit order details'}
              </button>
            </div>
          </form>
        )}

        {step === 'SUBMITTED' && (
          <div className="mandate-status-panel mandate-status-panel--success">
            <div className="mandate-status-icon">✓</div>
            <h2>Details submitted</h2>
            <p>
              Thanks — GenLend will register your debit order on NuPay. Watch
              for a bank prompt to approve GenLend, then we will confirm once
              it is authorised (SMS/email).
            </p>
            <ul className="mandate-status-hints">
              <li>You can still pay any due installment with PayFast.</li>
              <li>
                Status:{' '}
                <strong>pending</strong>
                {mandate?.accountReference
                  ? ` · Ref ${mandate.accountReference}`
                  : ''}
              </li>
            </ul>
            <div className="form-actions">
              <button
                type="button"
                className="submit-btn"
                onClick={() => navigate('/dashboard')}
              >
                Back to dashboard
              </button>
            </div>
          </div>
        )}

        {step === 'AUTHENTICATED' && (
          <div className="mandate-status-panel mandate-status-panel--success">
            <div className="mandate-status-icon">✓</div>
            <h2>Debit order authorised</h2>
            <p>
              Your DebiCheck mandate is authorised. Monthly repayments will be
              collected from your account on your chosen day.
            </p>
            {mandate && (
              <ul className="mandate-status-hints">
                <li>
                  <strong>Instalment amount:</strong>{' '}
                  {formatCurrency(mandate.collectionAmount)}
                </li>
                {mandate.firstCollectionDate && (
                  <li>
                    <strong>First collection:</strong>{' '}
                    {formatCollectionDate(mandate.firstCollectionDate)}
                  </li>
                )}
              </ul>
            )}
            <div className="form-actions">
              <button
                type="button"
                className="submit-btn"
                onClick={() => navigate('/dashboard')}
              >
                Back to dashboard
              </button>
            </div>
          </div>
        )}

        {step === 'ERROR' && (
          <div className="mandate-status-panel mandate-status-panel--error">
            <div className="mandate-status-icon">×</div>
            <h2>Something went wrong</h2>
            <p>{error || 'We could not complete setup right now.'}</p>
            <div className="form-actions">
              <button type="button" className="submit-btn" onClick={handleRetry}>
                Try again
              </button>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => navigate('/dashboard')}
              >
                Back to dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebitOrderSetup;
